import { FC, ReactNode, useMemo } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { formatNumber } from "../../../../../../../../common/utils";
import { ChartNoDataOverlay } from "../../../../../../../../components/charts/components/ChartNoDataOverlay";
import { GraphBarChartLegend } from "../../../../../../../../components/charts/components/GraphBarChartLegend";
import { EnergyChartItem } from "../ChangeEmissionTab/data/mappersVehiclesFuels";

type BarRow = {
	id: string;
	label: string;
	categoryLabel?: string;
	baseline: number;
	comparison: number;
};

type Props = {
	title: string;
	data?: EnergyChartItem[];
	baselineLabel?: string;
	comparisonLabel?: string;
	legendTitle?: string;
};

const ticks = [0, 20, 40, 60, 80, 100];

const labelWidth = 100;
const chartPaddingX = 2;
const barHeight = 12;
const barGap = 6;
const noDataHeight = 160;

const VehicleCategoryTotalsBarChart: FC<Props> = ({
	title,
	data,
	baselineLabel = "Baseline",
	comparisonLabel = "Comparison",
	legendTitle = "tkm %",
}) => {
	// Builds one row per normalized chart item.
	const rows = useMemo<BarRow[]>(
		() =>
			(data ?? []).map((item) => ({
				id: item.id,
				label: item.label,
				categoryLabel: item.categoryLabel,
				baseline: item.baseline.share.value,
				comparison: item.comparison.share.value,
			})),
		[data]
	);

	const hasNoData = rows.length === 0;

	// Removes label space when the chart has no rows.
	const chartLeftOffset = hasNoData ? 0 : labelWidth;

	const formatPercent = (value: number): string => `${formatNumber(value, "decimal", 1, 1)}%`;

	const renderTooltipRow = (label: string, value: number, color: string): ReactNode => (
		<Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
			<Stack direction="row" alignItems="center" gap={1}>
				<Box width={8} height={8} borderRadius="2px" bgcolor={color} flexShrink={0} />

				<Typography variant="caption">{label}</Typography>
			</Stack>

			<Typography variant="caption">{formatPercent(value)}</Typography>
		</Stack>
	);

	const renderTooltip = (item: BarRow): ReactNode => (
		<Stack p={1} minWidth={180}>
			<Typography variant="caption" fontWeight={600}>
				{item.label}
			</Typography>

			<Typography variant="caption">{item.categoryLabel}</Typography>

			{renderTooltipRow(baselineLabel, item.baseline, "primary.main")}

			{renderTooltipRow(comparisonLabel, item.comparison, "secondary.main")}
		</Stack>
	);

	return (
		<Stack gap={2} width="100%">
			<Typography variant="subtitle1" fontWeight={600}>
				{title}
			</Typography>

			{/* Chart */}
			<Stack gap={2} position="relative" pr={2}>
				{/* Vertical grid lines */}
				<Stack
					position="absolute"
					top={0}
					bottom={0}
					left={`calc(${chartLeftOffset}px + ${chartPaddingX * 8}px)`}
					right={`${chartPaddingX * 8}px`}
				>
					{ticks.map((tick) => (
						<Stack
							key={tick}
							position="absolute"
							left={`${tick}%`}
							top={0}
							bottom={0}
							width="1px"
							bgcolor="divider"
							sx={{
								transform: "translateX(-50%)",
								opacity: 0.75,
							}}
						/>
					))}
				</Stack>

				{/* Empty state */}
				{hasNoData && (
					<Box position="relative" width="100%" height={noDataHeight}>
						<ChartNoDataOverlay />
					</Box>
				)}

				{/* Rows */}
				{!hasNoData &&
					rows.map((item) => (
						<Stack key={item.id} direction="row" spacing={1} alignItems="center">
							{/* Row label */}
							<Stack width={labelWidth}>
								<Typography variant="caption" color="text.secondary" textAlign="right">
									{item.label}
								</Typography>
							</Stack>

							{/* Bar pair */}
							<Stack flex={1} px={chartPaddingX}>
								<Stack position="relative" height={barHeight * 2 + barGap}>
									<Tooltip
										arrow
										placement="right"
										title={renderTooltip(item)}
										slotProps={{
											tooltip: {
												sx: {
													bgcolor: "background.paper",
													color: "text.primary",
													boxShadow: 3,
													borderRadius: 1,
													p: 0,
													maxWidth: 300,
												},
											},
											arrow: {
												sx: {
													color: "background.paper",
												},
											},
										}}
									>
										<Stack
											position="absolute"
											top={0}
											left={0}
											height={barHeight}
											width={`${Math.min(Math.max(item.baseline, 0), 100)}%`}
											borderRadius={1}
											bgcolor="primary.main"
										/>
									</Tooltip>

									<Tooltip
										arrow
										placement="right"
										title={renderTooltip(item)}
										slotProps={{
											tooltip: {
												sx: {
													bgcolor: "background.paper",
													color: "text.primary",
													boxShadow: 3,
													borderRadius: 1,
													p: 0,
													maxWidth: 300,
												},
											},
											arrow: {
												sx: {
													color: "background.paper",
												},
											},
										}}
									>
										<Stack
											position="absolute"
											top={barHeight + barGap}
											left={0}
											height={barHeight}
											width={`${Math.min(Math.max(item.comparison, 0), 100)}%`}
											borderRadius={1}
											bgcolor="secondary.main"
										/>
									</Tooltip>
								</Stack>
							</Stack>
						</Stack>
					))}
			</Stack>

			{/* X axis labels */}
			<Stack direction="row" spacing={1} pr={2}>
				{!hasNoData && <Stack width={labelWidth} />}

				<Stack flex={1} position="relative" px={chartPaddingX} height={22}>
					{ticks.map((tick) => (
						<Typography
							key={tick}
							variant="caption"
							color="text.secondary"
							position="absolute"
							left={`${tick}%`}
							sx={{
								transform: "translateX(-50%)",
							}}
						>
							{tick}%
						</Typography>
					))}
				</Stack>
			</Stack>

			{/* Legend */}
			{!hasNoData && (
				<GraphBarChartLegend
					title={legendTitle}
					primaryLable={baselineLabel}
					secondaryLable={comparisonLabel}
					primaryColor="primary.main"
					secondaryColor="secondary.main"
				/>
			)}
		</Stack>
	);
};

export default VehicleCategoryTotalsBarChart;
